import { forwardRef } from 'react'
import { mappedArray } from '@10000/common'
import { useDelay } from '@10000/react'
import { Box, CenteredBox, CircleHeadings } from '..'
import { Slice } from '.'
import logo from '../../../assets/logo.svg'

export const Pie = forwardRef(function Pie({
  values, colors, size, headings, headingOpacity, showAllHeadings, transition,
  selectedSlice, onSliceClick, half, highlightedSegment, rotate, ...props
}, ref) {
  const showValue = useDelay()
  const totalAngle = half ? 180 : 360

  return (
    <CenteredBox
      container
      width={size}
      height={size}
      aspect="1/1"
      {...props}
    >
      {mappedArray(values.length, i => <Svg
        key={'bg-' + i}
        transition="0.25s"
        rotate={rotate}
        ref={ref}
        className="slice-background"
      >
        <Slice
          x={100}
          y={100}
          innerRadius={10}
          outerRadius={100}
          startAngle={totalAngle / values.length * i}
          sweepAngle={totalAngle / values.length}
          fill="#1E1E1E"
          onClick={() => onSliceClick?.(i)}
          transition={0}
          strokeWidth="0.1px"
          stroke="black"
        />
      </Svg>)}

      {values.map((sliceValue, i) =>
        /* the array/flat/map pattern allows for multiple values on each slice */
        [sliceValue].flat().map(((x, j, array) => {
          /* zIndex does not work within SVG elements, and reordering causes issues with transitioning slices.
             render a whole SVG for each slice so we can use zIndex */
          const zIndex = array.slice().sort().reverse().indexOf(x)
          const color = [colors[i]].flat()[j % [colors[i]].flat().length]

          return (
            <Svg
              key={`${i}${j}`}
              transition="0.25s"
              zIndex={zIndex}
              rotate={rotate}
              className="slice-value"
            >
              <Slice
                x={100}
                y={100}
                innerRadius={10}
                outerRadius={(showValue ? (x + 1) : 0) * 15 + 10}
                startAngle={totalAngle / values.length * i}
                sweepAngle={totalAngle / values.length}
                fill={color}
                transition={transition}
                opacity={(selectedSlice === undefined || selectedSlice === i) ? 1 : 0.25}
                fillOpacity={j === highlightedSegment ? 0.5 : 1}
                stroke={(j === highlightedSegment && typeof x === 'number') ? color : undefined}
                onClick={() => onSliceClick?.(i)}
              />
            </Svg>
          )
        }))
      )}

      <CenteredBox
        className="pie-headings"
        position="absolute"
        width="100%"
        height="100%"
        style={{ pointerEvents: 'none' }}
        rotate={rotate}
        transition={0.25}
      >
        <CircleHeadings
          headings={headings}
          values={values}
          opacity={headingOpacity}
          showAll={showAllHeadings}
        />
      </CenteredBox>

      <img src={logo} alt="logo" style={{ width: '7.5%', opacity: 0 }} className="logo" />
    </CenteredBox>
  )
})

const Svg = forwardRef(function Svg({ transition, zIndex, className, rotate, children }, ref) {
  return (
    <Box
      width="100%"
      height="100%"
      p="min(5%, 50px)"
      position="absolute"
      zIndex={zIndex}
      pointerEvents="none"
      className={className}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 200 200"
        style={{ rotate: rotate + 'deg', transition }}
        ref={ref}
      >
        {children}
      </svg>
    </Box>
  )
})